/**
 * QazBilim Backend API Service
 * Contains functions to interact with the backend API
 */

import { Book } from "./types";
import { API_ENDPOINTS } from "../constants";

/**
 * Uploads a document/image to the server
 * @param file The file to upload
 * @returns Promise with the uploaded file URL or error
 */
export const uploadDocument = async (file: File): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(API_ENDPOINTS.UPLOAD_URL, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed with status: ${response.status}`);
    }

    const data = await response.json();
    // API returns {success: true, data: {fileUrl: "...", ...}, message: "..."}
    return data.data?.fileUrl || data.fileUrl; // Support both response formats
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

/**
 * Creates a new book
 * @param payload Book data (id is generated by backend) plus userId (required by backend)
 * @returns Promise with the created book data or error
 */
export type CreateBookPayload = Omit<Book, "id"> & { userId: string };

export const createBook = async (payload: CreateBookPayload): Promise<Book> => {
  try {
    const response = await fetch(API_ENDPOINTS.BOOKS_URL, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.data as Book; // Backend returns {success: true, data: {...}, message: "..."}
  } catch (error) {
    console.error("Error creating book:", error);
    throw error;
  }
};

/**
 * Payload for updating an existing book.
 * Only updatable fields are included; id/userId/createdAt are managed by backend.
 */
export type UpdateBookPayload = Partial<
  Pick<
    Book,
    | "title"
    | "author"
    | "type"
    | "summary"
    | "coverUrl"
    | "language"
    | "content"
    | "accessType"
    | "published"
  >
>;

/**
 * Updates an existing book by ID.
 */
export const updateBook = async (
  id: string,
  payload: UpdateBookPayload,
): Promise<Book> => {
  try {
    const url = `${API_ENDPOINTS.BOOKS_URL}/${id}`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.data as Book;
  } catch (error) {
    console.error("Error updating book:", error);
    throw error;
  }
};

/**
 * Deletes a book by ID.
 */
export const deleteBook = async (id: string): Promise<void> => {
  const url = `${API_ENDPOINTS.BOOKS_URL}/${id}`;
  const response = await fetch(url, { method: "DELETE" });
  if (!response.ok) {
    throw new Error(`Error: ${response.status} ${response.statusText}`);
  }
};

/**
 * Fetches a list of books from the API
 * @param language Optional language filter
 * @param content Optional flag to include full content
 * @returns Promise with the list of books
 */
export const getBooks = async (
  language?: string,
  content?: boolean,
): Promise<Book[]> => {
  try {
    const params = new URLSearchParams();
    if (language) {
      params.append("language", language);
    }
    if (content !== undefined) {
      params.append("content", content.toString());
    }

    const url = `${API_ENDPOINTS.BOOKS_URL}${
      params.toString() ? `?${params.toString()}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.data as Book[]; // Backend returns {success: true, data: [...], message: "..."}
  } catch (error) {
    console.error("Error fetching books:", error);
    throw error;
  }
};

/**
 * Fetches books by authorId using the get_books_by_author_id endpoint
 * @param authorId The author ID to search for
 * @param language Optional language filter
 * @param content Optional flag to include full content
 * @returns Promise with the list of books for the author
 */
export const getBooksByAuthorId = async (
  authorId: string,
  language?: string,
  content?: boolean,
): Promise<Book[]> => {
  try {
    const params = new URLSearchParams();
    if (language) {
      params.append("language", language);
    }
    if (content !== undefined) {
      params.append("content", content.toString());
    }

    const url = `${API_ENDPOINTS.BOOKS_URL}/author/${authorId}${
      params.toString() ? `?${params.toString()}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return []; // Return empty array if no books found
      }
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    // Backend returns {success: true, data: Book | Book[], message: "..."}
    const books = Array.isArray(data.data) ? data.data : [data.data];
    return books as Book[];
  } catch (error) {
    console.error("Error fetching books by author ID:", error);
    throw error;
  }
};

/**
 * Fetches books by userId using the get_books_by_user_id endpoint
 * @param userId The user ID to fetch books for
 * @param language Optional language filter
 * @param content Optional flag to include full content
 * @returns Promise with the list of books for the user
 */
export const getBooksByUserId = async (
  userId: string,
  language?: string,
  content?: boolean,
): Promise<Book[]> => {
  try {
    const params = new URLSearchParams();
    if (language) {
      params.append("language", language);
    }
    if (content !== undefined) {
      params.append("content", content.toString());
    }

    const url = `${API_ENDPOINTS.BOOKS_URL}/user/${userId}${
      params.toString() ? `?${params.toString()}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    const books = Array.isArray(data.data) ? data.data : [];
    return books as Book[];
  } catch (error) {
    console.error("Error fetching books by user ID:", error);
    throw error;
  }
};

/**
 * Searches for books by name using the get_book_by_name endpoint
 * @param bookName The book name/title to search for
 * @param language Optional language filter
 * @param content Optional flag to include full content
 * @returns Promise with the list of matching books (or single book if one match)
 */
export const getBookByName = async (
  bookName: string,
  language?: string,
  content?: boolean,
): Promise<Book | Book[]> => {
  try {
    // URL encode the book name
    const encodedName = encodeURIComponent(bookName);
    const params = new URLSearchParams();
    if (language) {
      params.append("language", language);
    }
    if (content !== undefined) {
      params.append("content", content.toString());
    }

    const url = `${API_ENDPOINTS.BOOKS_URL}/name/${encodedName}${
      params.toString() ? `?${params.toString()}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return []; // Return empty array if no books found
      }
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    // Backend returns {success: true, data: Book | Book[], message: "..."}
    // If data is a single book, wrap it in an array for consistency
    const books = Array.isArray(data.data) ? data.data : [data.data];
    return books as Book[];
  } catch (error) {
    console.error("Error searching books by name:", error);
    throw error;
  }
};

/**
 * Fetches a specific book by author and title
 * @param author The author of the book
 * @param title The title of the book
 * @returns Promise with the book data or error
 */
export const fetchBookByAuthorAndTitle = async (
  author: string,
  title: string,
): Promise<Book> => {
  try {
    // Create a URL with query parameters
    const queryParams = new URLSearchParams({
      author,
      title,
    });

    const url = `${API_ENDPOINTS.BOOKS_URL}?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.data as Book; // Backend returns {success: true, data: {...}, message: "..."}
  } catch (error) {
    console.error("Error fetching book by author and title:", error);
    throw error;
  }
};

/**
 * Fetches a specific book by ID
 * @param id The unique identifier of the book
 * @param language The language code for the book content
 * @param content Optional flag to include full content
 * @returns Promise with the book data or error
 */
export const fetchBookById = async (
  id: string,
  language?: string,
  content?: boolean,
): Promise<Book> => {
  try {
    const params = new URLSearchParams();
    if (language) {
      params.append("language", language);
    }
    if (content !== undefined) {
      params.append("content", content.toString());
    }

    const url = `${API_ENDPOINTS.BOOKS_URL}/${id}${
      params.toString() ? `?${params.toString()}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.data as Book; // Backend returns {success: true, data: {...}, message: "..."}
  } catch (error) {
    console.error("Error fetching book by ID:", error);
    throw error;
  }
};

export type FetchBookWithLanguageFallbackResult = {
  book: Book;
  isLocalizationError: boolean;
  fallbackLanguage: string | null;
};

/**
 * Loads a book with full content for the reader. Tries the requested language first;
 * on failure, falls back to the canonical book (?content=true), same behavior as the legacy book store.
 */
export async function fetchBookWithLanguageFallback(
  id: string,
  language?: string,
): Promise<FetchBookWithLanguageFallbackResult> {
  try {
    const bookData = await fetchBookById(id, language, true);
    return {
      book: bookData,
      isLocalizationError: false,
      fallbackLanguage: null,
    };
  } catch {
    console.log("Book not found in requested language, trying fallback...");

    const url = `${API_ENDPOINTS.BOOKS_URL}/${id}?content=true`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const fallbackBookData = await response.json();

    let detectedLanguage = "unknown";
    let actualBookData = fallbackBookData;

    if (fallbackBookData && typeof fallbackBookData === "object") {
      if (fallbackBookData.data) {
        actualBookData = fallbackBookData.data;
      }

      if (actualBookData.language) {
        detectedLanguage = actualBookData.language;
      } else if (
        Array.isArray(actualBookData) &&
        actualBookData.length > 0 &&
        actualBookData[0].language
      ) {
        detectedLanguage = actualBookData[0].language;
      }
    }

    const raw = actualBookData;
    const book: Book = Array.isArray(raw) ? raw[0] : raw;
    if (!book || typeof book !== "object") {
      throw new Error("Invalid book payload");
    }

    return {
      book,
      isLocalizationError: true,
      fallbackLanguage: detectedLanguage,
    };
  }
}

/**
 * Fetches a list of authors from the API
 * @param language Optional language filter
 * @returns Promise with the list of authors
 */
export const getAuthors = async (language?: string): Promise<any[]> => {
  try {
    const params = new URLSearchParams();
    if (language) {
      params.append("language", language);
    }

    const url = `${API_ENDPOINTS.AUTHORS_URL}${
      params.toString() ? `?${params.toString()}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.data as any[]; // Backend returns {success: true, data: [...], message: "..."}
  } catch (error) {
    console.error("Error fetching authors:", error);
    throw error;
  }
};

/**
 * Fetches a specific author by ID
 * @param id The unique identifier of the author
 * @returns Promise with the author data or error
 */
export const fetchAuthorById = async (id: string): Promise<any> => {
  try {
    const url = `${API_ENDPOINTS.AUTHORS_URL}/${id}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.data as any; // Backend returns {success: true, data: {...}, message: "..."}
  } catch (error) {
    console.error("Error fetching author by ID:", error);
    throw error;
  }
};

/**
 * Creates a new author
 * @param author The author data to create (userId required by backend)
 * @returns Promise with the created author data or error
 */
export const createAuthor = async (author: {
  fullName: string;
  language: string;
  userId: string;
  firstName?: string;
  lastName?: string;
  birthYear?: number;
  deathYear?: number;
  bio?: string;
}): Promise<any> => {
  try {
    const response = await fetch(API_ENDPOINTS.AUTHORS_URL, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(author),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.data as any; // Backend returns {success: true, data: {...}, message: "..."}
  } catch (error) {
    console.error("Error creating author:", error);
    throw error;
  }
};

/**
 * Login with username and password
 * @param username The username
 * @param password The password
 * @returns Promise with login data including accessToken and userId
 */
export const login = async (
  username: string,
  password: string,
): Promise<{
  accessToken: string;
  expiresAt: string;
  userId: string;
  username: string;
  type?: string;
  firstName?: string;
  lastName?: string;
}> => {
  try {
    const response = await fetch(API_ENDPOINTS.USER_LOGIN_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    if (!data.success || !data.data) {
      throw new Error(data.message || "Login failed");
    }

    return data.data;
  } catch (error) {
    console.error("Error during login:", error);
    throw error;
  }
};

/** Language option for translate dropdown */
export type TranslateLanguage = { name: string; code: string };
export type ConvertDocumentHtmlPayload = {
  output_type: "html";
  pre_translate_html: string;
  translated_html: string;
  translated_applied: boolean;
};

/**
 * Fetches supported target languages for translation
 * @returns Promise with list of { name, code }
 */
/** Response from GET /convert/supported-types */
export type ConvertSupportedTypes = {
  allowed_extensions: string[];
  markdown_extensions: string[];
  /** Comma-separated list for HTML file input accept, e.g. ".pdf,.txt" */
  accept_markdown: string;
};

export const fetchConvertSupportedTypes =
  async (): Promise<ConvertSupportedTypes> => {
    const response = await fetch(API_ENDPOINTS.CONVERT_SUPPORTED_TYPES_URL, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    if (!data.success || data.data == null) {
      throw new Error(data.message || "Failed to load supported types");
    }
    const d = data.data as ConvertSupportedTypes;
    if (
      !Array.isArray(d.markdown_extensions) ||
      typeof d.accept_markdown !== "string"
    ) {
      throw new Error("Invalid supported-types response");
    }
    return d;
  };

export const getTranslateLanguages = async (): Promise<TranslateLanguage[]> => {
  try {
    const response = await fetch(API_ENDPOINTS.TRANSLATE_LANGUAGES_URL, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    if (!data.success || !Array.isArray(data.data)) {
      throw new Error(data.message || "Failed to load languages");
    }
    return data.data as TranslateLanguage[];
  } catch (error) {
    console.error("Error fetching translate languages:", error);
    throw error;
  }
};

/**
 * Converts an uploaded file (html or markdown output).
 * @param file The file to convert (pdf, docx, txt, etc.)
 * @param options Optional conversion options
 * @returns Promise with converted content string
 */
/**
 * Splits document text into multiple chapter sections via POST /split-chapters (delimiter line mode).
 */
export const splitChaptersText = async (
  text: string,
  options: {
    delimiter: string;
    strip_standalone_numbers?: boolean;
  },
): Promise<{ chapter: string; text: string }[]> => {
  try {
    const response = await fetch(API_ENDPOINTS.SPLIT_CHAPTERS_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text,
        mode: "delimiter",
        delimiter: options.delimiter,
        strip_standalone_numbers: options.strip_standalone_numbers ?? true,
      }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Split failed: ${response.status}`);
    }
    const data = await response.json();
    if (!data.success || data.data == null) {
      throw new Error(data.message || "Split failed");
    }
    const chapters = data.data.chapters;
    if (!Array.isArray(chapters)) {
      throw new Error("Invalid split response");
    }
    return chapters as { chapter: string; text: string }[];
  } catch (error) {
    console.error("Error splitting chapters:", error);
    throw error;
  }
};

export const convertDocument = async (
  file: File,
  options?: {
    output_type?: "html" | "markdown";
    target_lang?: string;
    source_lang?: string;
  },
): Promise<string | ConvertDocumentHtmlPayload> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("output_type", options?.output_type ?? "html");
    if (options?.target_lang) {
      formData.append("target_lang", options.target_lang);
    }
    if (options?.source_lang) {
      formData.append("source_lang", options.source_lang);
    }
    const response = await fetch(API_ENDPOINTS.CONVERT_URL, {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Convert failed: ${response.status}`);
    }
    const data = await response.json();
    if (!data.success || data.data == null) {
      throw new Error(data.message || "Conversion failed");
    }
    if (typeof data.data === "string") {
      return data.data;
    }
    return data.data as ConvertDocumentHtmlPayload;
  } catch (error) {
    console.error("Error converting document:", error);
    throw error;
  }
};

/**
 * Translates text to a target language
 * @param text Text to translate
 * @param target_lang Target language code (e.g. 'es', 'ru')
 * @param source_lang Optional source language; default 'auto'
 * @returns Promise with translated text
 */
export const translateText = async (
  text: string,
  target_lang: string,
  source_lang: string = "auto",
): Promise<string> => {
  try {
    const response = await fetch(API_ENDPOINTS.TRANSLATE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text,
        target_lang,
        source_lang,
      }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Translate failed: ${response.status}`);
    }
    const data = await response.json();
    if (!data.success || data.data == null) {
      throw new Error(data.message || "Translation failed");
    }
    return typeof data.data === "string" ? data.data : String(data.data);
  } catch (error) {
    console.error("Error translating text:", error);
    throw error;
  }
};
